import React from "react";
import { Link } from "react-router-dom";
import { Presentation, ClipboardCheck, Users, Globe2, Handshake, Award, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHero, Section, SectionHeading } from "@/components/blocks";

const SERVICES = [
  { icon: Presentation, title: "Workshop Delivery", desc: "Professional learning workshops delivered to your cohort, on campus or online." },
  { icon: ClipboardCheck, title: "Placement Readiness Programs", desc: "Structured programs that ensure your students are prepared for professional experience." },
  { icon: Users, title: "Mentor Training", desc: "Equip supervising teachers with the frameworks to mentor effectively." },
  { icon: Globe2, title: "International PST Preparation", desc: "Specialist preparation for international pre-service teachers." },
  { icon: Handshake, title: "Partnership Support", desc: "Strengthen school–university partnerships with a proven framework." },
];

const FUTURE = ["Placement Ready Certificate", "Mentor Accreditation", "Partnership Framework Resources"];

export default function Universities() {
  return (
    <div data-testid="universities-page">
      <PageHero
        eyebrow="For Universities"
        title="A professional learning & partnership organisation."
        subtitle="Teaching Pathways partners with initial teacher education providers to deliver placement-ready graduates and stronger school relationships."
        image="https://images.pexels.com/photos/5940842/pexels-photo-5940842.jpeg?auto=compress&cs=tinysrgb&w=1600"
      >
        <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 rounded-full px-6"><Link to="/contact" data-testid="uni-hero-cta">Discuss a partnership <ArrowRight className="w-4 h-4" /></Link></Button>
      </PageHero>

      <Section>
        <SectionHeading eyebrow="Services" title="How we partner with universities" center />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <Card key={s.title} className="p-8 border-slate-200 hover:shadow-lg transition-shadow" data-testid={`uni-service-${s.title}`}>
              <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mb-5"><s.icon className="w-6 h-6 text-white" /></div>
              <h3 className="font-display text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
            </Card>
          ))}
        </div>
      </Section>

      <div className="bg-slate-900">
        <Section>
          <div className="text-center max-w-2xl mx-auto">
            <Badge className="bg-teal-500/20 text-teal-300 hover:bg-teal-500/20 border border-teal-500/30 mb-4"><Award className="w-3.5 h-3.5 mr-1" /> Coming Soon</Badge>
            <h2 className="font-display text-3xl font-bold text-white">Future offerings</h2>
            <p className="text-slate-300 mt-3">We are building accredited frameworks to formalise placement readiness and mentoring across the sector.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {FUTURE.map((f) => (
              <div key={f} className="rounded-xl border border-slate-700 bg-slate-800/50 p-6 text-center" data-testid={`uni-future-${f}`}>
                <Award className="w-7 h-7 text-emerald-400 mx-auto mb-3" />
                <p className="font-display font-semibold text-white">{f}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
